import React, { useEffect, useRef } from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";

export const BinaryBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const cols = 26; // Number of vertical binary streams

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Premium dark-mode tech background fill
    ctx.fillStyle = "#040409";
    ctx.fillRect(0, 0, width, height);

    // Deep ambient neon cyan glow in the lower center
    const glowX = width / 2;
    const glowY = height * 0.7;
    const ambientGlow = ctx.createRadialGradient(
      glowX,
      glowY,
      100,
      glowX,
      glowY,
      800,
    );
    ambientGlow.addColorStop(0, "rgba(0, 242, 254, 0.08)");
    ambientGlow.addColorStop(0.5, "rgba(157, 78, 221, 0.04)");
    ambientGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = ambientGlow;
    ctx.fillRect(0, 0, width, height);

    // Draw Cyberpunk subtle horizontal scanlines
    ctx.strokeStyle = "rgba(255, 255, 255, 0.005)";
    ctx.lineWidth = 2;
    for (let y = 0; y < height; y += 12) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Monospace tech typography for streaming binary code characters
    ctx.font = "bold 22px 'Courier New', Courier, monospace";
    ctx.textBaseline = "top";

    // Draw 16 columns of desynchronized binary character streams
    for (let c = 0; c < cols; c++) {
      // Deterministic seeds based on column index
      const seedX = Math.sin(c * 532.74) * 1000;
      const seedSpeed = Math.cos(c * 142.19) * 0.5 + 1.2; // Speed offset

      const colX = (Math.abs(seedX) % (width - 120)) + 60;
      const charHeight = 28;
      const maxRows = Math.floor(height / charHeight);

      // Speed of streaming down
      const currentScroll = (frame * seedSpeed * 0.25) % maxRows;

      for (let r = 0; r < maxRows; r++) {
        const charY = r * charHeight;

        // Deterministic character selection (0 or 1) based on column, row and frame scroll
        const charSeed = Math.sin(
          c * 23.4 + r * 81.3 + Math.floor(frame / 6) * 0.1,
        );
        const char = charSeed > 0 ? "1" : "0";

        // Fade effect: streams are brighter near their leading edge (currentScroll)
        const distance = (r - currentScroll + maxRows) % maxRows;

        // Deep streaming opacity (keeps background subtle and non-distracting)
        let opacity = 0;
        if (distance < 12) {
          opacity = 0.07 * (1.0 - distance / 12);
        } else {
          opacity = 0.012; // Base ambient background noise
        }

        // Active highlighted leading character (very low probability glow for realism)
        const isGlowingLeader = Math.floor(currentScroll) === r && c % 3 === 0;

        if (isGlowingLeader) {
          ctx.fillStyle = "rgba(0, 242, 254, 0.25)";
          // Apply a minor canvas text glow
          ctx.shadowBlur = 8;
          ctx.shadowColor = "#00f2fe";
        } else {
          ctx.fillStyle =
            c % 2 === 0
              ? `rgba(0, 242, 254, ${opacity})`
              : `rgba(157, 78, 221, ${opacity})`;
          ctx.shadowBlur = 0;
        }

        ctx.fillText(char, colX, charY);
      }
    }

    // Reset shadow blur
    ctx.shadowBlur = 0;
  }, [frame, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
